<template>
  <div class="chat">
    <div class="users-list-container chat">
      <ul class="users-list">
        <li
          v-on:keyup.enter="$event.target.click()"
          tabindex="0"
          @click="openChat(authorKey)"
          v-for="({ authorThemeKey, timestamp, authorKey, nameTagKey }, idx) in chats"
          data-testid="chat-list-item"
          :key="`${authorKey}-${idx}`"
        >
          <div>
            <authorInitial :author-name="nameTagKey" :theme-key="authorThemeKey"></authorInitial>
            <span class="chat-info">
              {{ nameTagKey }}
              <br />
              <small>{{ timestamp }}</small>
            </span>
          </div>
        </li>
      </ul>
    </div>
    <template v-for="({ nameTagKey, authorKey, messages }, idx) in chatBoxes">
      <chat-box
        @close="closeChat(authorKey)"
        @resize="changeResizeStatus(authorKey)"
        @minimize="changeMinimizeStatus(authorKey)"
        @click="clickChat(authorKey)"
        :rightPos="rightPosArr[idx]"
        :idx="idx"
        :messages="messages"
        :author="nameTagKey"
        :isExpanded="expandedConvos[authorKey] ? true : false"
        :isOpen="minimizedConvos[authorKey] ? false : true"
        :key="`chat-${authorKey}`"
        :has-new="!!newConvos[authorKey]"
      />
    </template>
  </div>
</template>
<script>
  import Vue from 'vue';
  import authorInitial from '../AuthorInitial.vue';
  import chatBox from './ChatBox.vue';
  import { logUserAction } from '@assessment/../api/log';
  import { mapState } from 'vuex';
  import { ASSESSMENT_TYPES } from '@assessment/../store/constants';
  export default {
    props: {
      deliveredMessages: {
        type: Array,
        required: false,
        default: [],
      },
    },

    data() {
      return {
        conversationOpen: false,
        initial: true,
        conversations: {},
        newConvos: {},
        minimizedConvos: {},
        expandedConvos: {},
        clickedConvos: {},
      };
    },

    components: {
      authorInitial,
      chatBox,
    },

    computed: {
      ...mapState(['assessmentTypeKey', 'versionConfig', 'hasPhases', 'simsettings']),
      chats() {
        const messagesByAuthor = this.deliveredMessages.reduce((retVal, message) => {
          const {
            authorKey,
            authorNameTagKey: nameTagKey,
            timestamp,
            timer,
            authorThemeKey,
          } = message;
          if (!(authorKey in retVal))
            retVal[authorKey] = {
              authorKey,
              nameTagKey,
              authorThemeKey,
              timestamp: '',
              latestTimer: 0,
              messages: [],
            };

          this.$set(retVal[authorKey], 'messages', [...retVal[authorKey].messages, message]);

          // pick the latestTimer and timestamp from the same authors to display on the left navigation
          if (
            !retVal[authorKey].latestTimer ||
            (retVal[authorKey].latestTimer && retVal[authorKey].latestTimer < timer)
          ) {
            retVal[authorKey].timestamp = timestamp;
            retVal[authorKey].latestTimer = timer;
          }

          return retVal;
        }, {});
        return Object.values(messagesByAuthor).sort((a, b) => b.latestTimer - a.latestTimer);
      },
      allowReEntry() {
        return (
          (this.simsettings !== null &&
            Object.keys(this.simsettings).length > 0 &&
            this.simsettings.allowReEntryInbox !== undefined) ||
          this.versionConfig.allowReEntryInbox
        );
      },

      chatBoxes() {
        return this.chats.filter((chat) => this.conversations[chat.authorKey]);
      },

      rightPosArr() {
        const rightPos = [];
        let expanded = false;
        for (let i = 0; i < this.chatBoxes.length; i += 1) {
          const lastPos = rightPos.length ? rightPos[rightPos.length - 1] : 0;
          let pos = lastPos;
          if (i === 0) {
            pos += 30;
          } else if (expanded) {
            pos += this.$store.getters.constants['expanded-chat-width'] + 10;
          } else {
            pos += this.$store.getters.constants['chat-width'] + 10;
          }
          rightPos.push(pos);
          expanded = this.expandedConvos[this.chatBoxes[i].authorKey];
        }
        return rightPos;
      },
    },

    watch: {
      deliveredMessages(val, oldVal) {
        if (val.length > oldVal.length) {
          this.openChat(val[val.length - 1].authorKey);
          this.setNewMsgStatus(val[val.length - 1].authorKey, true);
        }
      },
    },

    methods: {
      setAuthors() {
        // if it's webapp and allowReEntryInbox is on, then use the time train logic implemented
        if (this.allowReEntry && this.assessmentTypeKey === ASSESSMENT_TYPES.WEBAPP) {
          this.$store.getters['chat/messages'].forEach(
            ({ authorKey, userActivityStatus, questionKey, isRead }) => {
              const { isMessageExpanded, isMessageMinimzed, isMessageOpened } = userActivityStatus;
              this.$set(this.conversations, authorKey, isMessageOpened);
              this.$set(this.expandedConvos, authorKey, isMessageExpanded);
              this.$set(this.minimizedConvos, authorKey, isMessageMinimzed);
              this.$set(this.clickedConvos, questionKey, isRead);
            }
          );
        } else {
          this.$store.getters['chat/messages'].forEach((m) =>
            this.$set(this.conversations, m.authorKey, true)
          );
        }
      },

      async changeResizeStatus(name) {
        Vue.set(this.expandedConvos, name, !this.expandedConvos[name]);
        const lastChatDisplayed = this.getLastChatDisplayed(name);
        await this.logAction({ actionKey: 17, objectKey: lastChatDisplayed.questionKey });
      },

      async changeMinimizeStatus(name) {
        Vue.set(this.minimizedConvos, name, !this.minimizedConvos[name]);
        const lastChatDisplayed = this.getLastChatDisplayed(name);
        await this.logAction({ actionKey: 16, objectKey: lastChatDisplayed.questionKey });
      },

      async closeChat(name) {
        this.$set(this.conversations, name, false);
        this.$set(this.expandedConvos, name, false);
        this.setNewMsgStatus(name, false);
        //chats
        const lastChatDisplayed = this.getLastChatDisplayed(name);
        await this.logAction({ actionKey: 18, objectKey: lastChatDisplayed.questionKey });
      },

      setNewMsgStatus(authorKey, status) {
        this.$set(this.newConvos, authorKey, status);
      },

      async openChat(name) {
        // use if statement to prevent user to click open message multiple times
        if (!this.conversations[name]) {
          const lastChatDisplayed = this.getLastChatDisplayed(name);
          await this.logAction({ actionKey: 4, objectKey: lastChatDisplayed.questionKey });
        }
        this.$set(this.conversations, name, true);
      },

      async clickChat(authorKey) {
        const lastChatDisplayed = this.getLastChatDisplayed(authorKey);
        this.setNewMsgStatus(authorKey, false);
        if (!this.clickedConvos[lastChatDisplayed.questionKey]) {
          this.clickedConvos[lastChatDisplayed.questionKey] = true;
          await this.logAction({ actionKey: 19, objectKey: lastChatDisplayed.questionKey });
        }
      },

      submitAnswer() {
        this.$store.dispatch('ANSWER_QUESTION', {
          questionType: 'messages',
          questionkey: this.needsAnswer.questionKey,
          answerkey: this.picked,
        });
      },

      getLastChatDisplayed(name) {
        const closedChat = this.chats.find(({ authorKey }) => authorKey === name);
        return closedChat.messages[closedChat.messages.length - 1];
      },

      async logAction({ actionKey, objectKey }) {
        // only if it's webapp and allowReEntryInbox is on, then use the time train logic implemented
        if (this.allowReEntry && this.assessmentTypeKey === ASSESSMENT_TYPES.WEBAPP) {
          await logUserAction({ actionKey, objectKey });
        }
      },
    },

    created() {
      this.setAuthors();
    },

    mounted() {
      setTimeout(() => (this.initial = false), 2000);
    },
  };
</script>
<style lang="scss" scoped>
  .chat {
    background: #1d2430;
    z-index: 1000;
    border-left: 1px solid rgba(0, 0, 0, 0.2);
    .users-list-container {
      overflow-y: scroll;
      height: auto;

      z-index: 1001;
      ul {
        margin: 0;
        padding: 0;
        list-style: none;
        li {
          padding: 10px 20px;
          &:hover {
            background-color: #171d25;
          }
          span {
            display: inline-block;
            vertical-align: middle;
            &.chat-info {
              color: #999;
              padding-left: 5px;
              small {
                color: #ccc;
              }
            }
          }
        }
      }
    }
  }
  @media (max-height: 768px) {
    .users-list-container {
      max-height: 75px;
    }
  }
  @media (min-height: 769px) {
    .users-list-container {
      max-height: 210px;
    }
  }
  @media (min-height: 1080px) {
    .users-list-container {
      max-height: 350px;
    }
  }
</style>
